import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, TrendingUp, Award, Target, Zap, Star, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { PageShell } from "@/components/altiora/PremiumUI";

const SCORE_META = {
  maturity:    { Icon: Award,      tone: "#0B2545", accent: "#EEF4FF" },
  trajectory:  { Icon: Target,     tone: "#7A5B14", accent: "#FBF6EA" },
  progression: { Icon: TrendingUp, tone: "#0B7A3B", accent: "#DCFCE7" },
  execution:   { Icon: Zap,        tone: "#B45309", accent: "#FEF3C7" },
  potential:   { Icon: Star,       tone: "#7C3AED", accent: "#EDE9FE" },
};

export default function AbiExcellence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [explainer, setExplainer] = useState(null);

  const explainScore = async (key) => {
    try {
      const r = await api.get(`/abi/copilot/excellence/scores/${key}/explain`);
      setExplainer(r.data);
    } catch { toast.error("Erreur"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/abi/copilot/excellence/assessment");
      setData(r.data);
    } catch (e) { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const selfAssess = async (family_id, value) => {
    setSaving(true);
    try {
      await api.post("/abi/copilot/excellence/selfassess",
                      { scores: { [family_id]: value } });
      toast.success("Auto-évaluation enregistrée.");
      await load();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  if (loading || !data) return (
    <div className="p-8 flex items-center gap-2" data-testid="abi-excellence-loading">
      <Loader2 size={16} className="animate-spin" /> Chargement…
    </div>
  );

  return (
    <PageShell testid="abi-excellence-root">
      <TopBar />

      {/* Hero */}
      <div className="px-8 pt-8 pb-6"
            style={{ background: "linear-gradient(120deg,#0B2545 0%,#123B6E 60%,#1D5AA5 100%)" }}>
        <div className="max-w-6xl mx-auto text-ivory">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest opacity-80">
            <Sparkles size={14} /> ALTIORA · Système d'Optimisation de Trajectoire
          </div>
          <h1 className="text-3xl md:text-4xl font-serif mt-2 leading-tight text-white"
              data-testid="abi-excellence-title">
            Vos compétences, mesurées et développées
          </h1>
          <p className="text-white/85 mt-3 max-w-3xl italic">
            {data.philosophy_fr}
          </p>
          {data.confidence && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-[12px]"
                  style={{ backgroundColor: "rgba(197,168,128,0.20)", color: "#F5D687" }}
                  data-testid="abi-excellence-confidence"
                  title={(data.confidence.factors_fr || []).join(" · ")}>
              ⛨ Indice de confiance du diagnostic · {data.confidence.label_fr}
              {data.confidence.warning_fr && (
                <span className="ml-1 opacity-80">— {data.confidence.warning_fr}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 5 trajectory scores */}
      <div className="max-w-6xl mx-auto px-8 py-6 grid grid-cols-2 md:grid-cols-5 gap-3"
            data-testid="abi-excellence-scores">
        {Object.entries(data.trajectory_scores || {}).map(([key, s]) => {
          const m = SCORE_META[key] || { Icon: Star, tone: "#0B2545", accent: "#EEF4FF" };
          const Icon = m.Icon;
          return (
            <button key={key} onClick={() => explainScore(key)}
                    className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition text-left"
                    style={{ borderLeft: `4px solid ${m.tone}` }}
                    data-testid={`abi-excellence-score-${key}`}>
              <div className="w-8 h-8 rounded flex items-center justify-center mb-2"
                    style={{ backgroundColor: m.accent, color: m.tone }}>
                <Icon size={16} />
              </div>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: m.tone }}>
                {s.label_fr}
              </div>
              <div className="font-serif text-3xl font-bold mt-1" style={{ color: "#0B2545" }}>
                {s.score.toFixed(0)}
                <span className="text-sm font-normal text-slate-500">/100</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">
                Voir les preuves →
              </div>
            </button>
          );
        })}
      </div>

      {/* Score explainer modal */}
      {explainer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setExplainer(null)}
              data-testid="abi-score-explainer">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b" style={{
                  background: "linear-gradient(120deg,#0B2545 0%,#123B6E 100%)",
                  borderColor: "#E5E7EB" }}>
              <div className="text-white">
                <div className="text-[10px] uppercase tracking-widest opacity-80">
                  Score expliqué · Doctrine v5 · Aucun score arbitraire
                </div>
                <h2 className="font-serif text-2xl mt-0.5" data-testid="abi-explainer-title">
                  {explainer.label_fr} · {explainer.current_value.toFixed(0)}/100
                </h2>
              </div>
            </div>
            <div className="p-5 space-y-4 text-[14px]">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1"
                      style={{ color: "#7A5B14" }}>Formule de calcul</div>
                <p className="text-slate-800 italic">{explainer.formula_fr}</p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-2"
                      style={{ color: "#0B2545" }}>
                  Événements observés · {explainer.window_days} derniers jours
                </div>
                <div className="space-y-2" data-testid="abi-explainer-movers">
                  {(explainer.movers || []).map((mv, i) => {
                    const c = mv.kind === "positive" ? "#0B7A3B"
                                : mv.kind === "negative" ? "#B91C1C" : "#7A5B14";
                    const bg = mv.kind === "positive" ? "#DCFCE7"
                                : mv.kind === "negative" ? "#FEE2E2" : "#FBF6EA";
                    return (
                      <div key={i} className="p-3 rounded flex items-start gap-3"
                            style={{ backgroundColor: bg, borderLeft: `4px solid ${c}` }}
                            data-testid={`abi-explainer-mover-${i}`}>
                        <span className="font-mono text-[11px] px-1.5 py-0.5 rounded flex-shrink-0"
                                style={{ backgroundColor: c, color: "#fff" }}>
                          {mv.impact_pts >= 0 ? "+" : ""}{mv.impact_pts.toFixed(1)}
                        </span>
                        <div className="flex-1">
                          <div className="text-slate-900">{mv.text_fr}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            Source · {mv.source}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(explainer.movers || []).length === 0 && (
                    <div className="italic text-slate-500">Pas encore assez d'événements observés pour expliquer ce score.</div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-4 border-t text-right" style={{ borderTopColor: "#E5E7EB" }}>
              <button onClick={() => setExplainer(null)}
                      className="px-4 py-1.5 rounded text-[13px]"
                      style={{ backgroundColor: "#0B2545", color: "#fff" }}
                      data-testid="abi-explainer-close">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top-3 priority skills — the flagship section */}
      <div className="max-w-6xl mx-auto px-8 pb-6">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden"
              data-testid="abi-excellence-priorities">
          <div className="p-5 border-b" style={{
                background: "linear-gradient(120deg,#7A5B14 0%,#C5A880 100%)",
                borderColor: "#E5E7EB" }}>
            <div className="text-white">
              <div className="text-[10px] uppercase tracking-widest opacity-80">
                Les 3 compétences prioritaires
              </div>
              <h2 className="font-serif text-2xl mt-0.5">
                À travailler pendant les 90 prochains jours
              </h2>
            </div>
          </div>
          <div className="p-5 space-y-4">
            {(data.priority_skills || []).map((s, i) => (
              <div key={s.family_id}
                    className="rounded-lg p-4 border-l-4 bg-slate-50"
                    style={{ borderLeftColor: "#7A5B14" }}
                    data-testid={`abi-excellence-priority-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#7A5B14" }}>
                      Priorité {i + 1}
                    </div>
                    <h3 className="font-serif text-xl mt-0.5" style={{ color: "#0B2545" }}>
                      {s.label_fr}
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">Score</div>
                    <div className="font-mono text-lg" style={{ color: "#B91C1C" }}>
                      {s.current_score}
                    </div>
                    <div className="text-[10px] text-slate-500">→ cible {s.target_score}</div>
                  </div>
                </div>
                <p className="text-[13px] text-slate-800 mt-2">
                  {s.why_selected_fr}
                </p>
                {(s.exercises_fr || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "#0B7A3B" }}>
                      Exercices concrets
                    </div>
                    <ul className="text-[13px] space-y-1">
                      {s.exercises_fr.map((e, k) => (
                        <li key={k} className="text-slate-800">→ {e}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(s.resources_fr || []).length > 0 && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <GraduationCap size={12} style={{ color: "#7A5B14" }} />
                    {s.resources_fr.map((r, k) => (
                      <span key={k} className="text-[11px] px-2 py-1 rounded"
                            style={{ backgroundColor: "#FBF6EA", color: "#7A5B14" }}>
                        📖 {r}
                      </span>
                    ))}
                  </div>
                )}
                {(s.knowledge_cards || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1"
                          style={{ color: "#7C3AED" }}>
                      Fiches APKE liées · fondements scientifiques
                    </div>
                    <div className="flex flex-wrap gap-2"
                          data-testid={`abi-excellence-priority-${i}-kb`}>
                      {s.knowledge_cards.map((c, k) => (
                        <a key={k} href={`/app/abi/knowledge`}
                            className="text-[11px] px-2 py-1 rounded border transition"
                            style={{ borderColor: "#7C3AED", color: "#7C3AED",
                                      backgroundColor: "#EDE9FE" }}>
                          ⛨ {c.title_fr} · Preuve {c.evidence_level}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {(data.priority_skills || []).length === 0 && (
              <div className="text-center text-slate-500 italic py-4">
                Toutes vos compétences sont au-dessus du seuil critique — beau travail.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 20 skill families with self-assess */}
      <div className="max-w-6xl mx-auto px-8 pb-10">
        <div className="bg-white rounded-lg shadow-sm p-5" data-testid="abi-excellence-families">
          <h2 className="font-serif text-xl" style={{ color: "#0B2545" }}>
            Les 20 familles de compétences
          </h2>
          <p className="text-[13px] text-slate-600 mb-4">
            Cliquez sur une note (1-5) pour renseigner votre auto-évaluation.
            Le moteur combine votre auto-évaluation, vos signaux, le benchmark et
            votre mémoire pour produire un score composite.
          </p>
          <div className="grid md:grid-cols-2 gap-2">
            {Object.entries(data.family_scores || {})
              .sort((a, b) => a[1].composite - b[1].composite)
              .map(([fid, f]) => {
                const barColor = f.composite >= 75 ? "#0B7A3B"
                                    : f.composite <= 50 ? "#B91C1C" : "#7A5B14";
                const current = data.self_scores?.[fid];
                return (
                  <div key={fid} className="border rounded p-2"
                        style={{ borderColor: "#E5E7EB" }}
                        data-testid={`abi-excellence-family-${fid}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-[13px] font-semibold" style={{ color: "#0B2545" }}>
                        {f.label_fr}
                      </div>
                      <div className="text-[11px] font-mono" style={{ color: barColor }}>
                        {f.composite}/100
                      </div>
                    </div>
                    <div className="flex-1 bg-gray-100 h-1.5 rounded overflow-hidden mb-2">
                      <div className="h-full" style={{
                        width: `${f.composite}%`,
                        backgroundColor: barColor,
                      }} />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-slate-500 mr-1">Auto-éval :</span>
                      {[1, 2, 3, 4, 5].map(v => (
                        <button key={v}
                                onClick={() => selfAssess(fid, v)}
                                disabled={saving}
                                className="text-[11px] w-6 h-6 rounded transition"
                                style={{
                                  backgroundColor: current === v ? "#0B2545" : "#F3F4F6",
                                  color: current === v ? "#C5A880" : "#5B5B62",
                                }}
                                data-testid={`abi-excellence-selfassess-${fid}-${v}`}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
